<template>
  <div v-style:backgroundColor="backgroundColor"
       v-style:display="imageOverlapping ? 'flow-root' : 'block'"
       v-style:maxWidth="maxWidth"
       style="border-radius: 14px; position: relative;">

    <v-btn v-if="infoText"
           class="info-btn"
           icon
           @click="displayInfo = !displayInfo">
      <v-icon v-if="!displayInfo" class="info-icon" color="#fff">
        icon-information
      </v-icon>
      <v-icon v-else class="close-icon" color="#fff">
        icon-close
      </v-icon>
    </v-btn>

    <template v-if="displayInfo">
      <v-img v-if="image"
             :src="require('@/assets/onboarding/fastTrack/' + image)"
             class="card-image"
             contain
             style="position: absolute; opacity: 0.1;"/>
      <div class="content-padding info-text"
           v-html="infoText"/>
    </template>

    <template v-else>
      <template v-if="!headerImageInRow">
        <v-img v-if="image"
               v-style:marginTop="imageOverlapping ? '-48px' : '0'"
               v-style:paddingTop="infoText ? '60px' : '0'"
               :src="require('@/assets/onboarding/fastTrack/' + image)"
               class="card-image"
               contain/>

        <h4 v-if="smallHeader"
            class="small-header pb-3 content-padding">
          {{ smallHeader }}
        </h4>

        <h2 v-if="header"
            class="header pb-6 content-padding">
          {{ header }}
        </h2>
      </template>

      <template v-else>
        <v-row class="pt-6" no-gutters>
          <v-col cols="7">
            <h4 v-if="smallHeader"
                class="small-header pb-3 content-padding">
              {{ smallHeader }}
            </h4>

            <h2 v-if="header"
                class="header pb-6 content-padding">
              {{ header }}
            </h2>
          </v-col>
          <v-col align-self="center" cols="5">
            <v-img v-if="image"
                   v-style:marginTop="imageOverlapping ? '-48px' : '0'"
                   v-style:paddingTop="infoText ? '60px' : '0'"
                   :src="require('@/assets/onboarding/fastTrack/' + image)"
                   contain/>
          </v-col>
        </v-row>
      </template>
    </template>

    <v-btn v-if="buttonLabel && buttonAction"
           :dark="buttonOutlined"
           :outlined="buttonOutlined"
           class="action-btn"
           @click="callButtonAction">
      {{ buttonLabel }}
    </v-btn>
  </div>
</template>

<script>
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: "FinOnboardingFastTrackCard",
  props: {
    /** Sets the background color of the card */
    backgroundColor: {
      type: String,
      default: '#1A2363'
    },
    /** Sets the max width of the card */
    maxWidth: {
      type: String,
      default: '440px'
    },
    /** Sets the small header text (displayed above the header, text-transform uppercase) */
    smallHeader: {
      type: String
    },
    /** Sets the header text */
    header: {
      type: String
    },
    /** Sets the image name (base dir @/assets/onboarding/fastTrack) */
    image: {
      type: String
    },
    /** Sets if the image should overlap the card box (don't use with infoText) */
    imageOverlapping: {
      type: Boolean,
      default: false
    },
    /** Sets the label of the button (to be visible a buttonAction must be set too) */
    buttonLabel: {
      type: String
    },
    /** Changes the button display to outlined */
    buttonOutlined: {
      type: Boolean,
      default: false
    },
    /** Sets the router object to be called via router.push when the button is clicked */
    buttonAction: {
      type: Object
    },
    /** If set the info button is shown + on click the infoText is displayed as html (h2 / h4 / p / ul-li can be used) */
    infoText: {
      type: String
    },
    /** If set the header + image is displayed in a row with 2 cols 7/5 (instead the default = beneath) */
    headerImageInRow: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      displayInfo: false,
    }
  },
  mixins: [AmplitudePush],
  methods: {
    callButtonAction() {
      if (this.buttonAction) {
        // ignore nav errors like Uncaught (in promise) Error: Redirected when going from -> see SignIn
        this.$emit('fastTrackButtonClicked')
        this.$router.push(this.buttonAction).catch(() => {
        })
        this.pushToAmplitude({
          'event': "fincraft_fasttrack_starting",
          'step': this.buttonAction.name,
          'placement': 'fast_track',
          'vue_breakpoint': this.$vuetify.breakpoint.name
        })
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.info-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 10;
}

.info-text {
  padding-top: 48px;
  padding-bottom: 48px;
}

.small-header,
.info-text ::v-deep .small-header {
  font-family: $body-font-family;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 28px;
  letter-spacing: 1.1375px;
  text-transform: uppercase;
  color: $fin-color-primary-light;

  @media only screen and (max-width: 600px) {
    font-size: 10px;
    line-height: 14px;
  }
}

.header,
.info-text ::v-deep .header {
  font-family: $heading-font-family;
  font-style: normal;
  font-weight: normal;
  font-size: 36px;
  line-height: 44px;
  color: $fin-color-primary-light;

  @media only screen and (max-width: 960px) {
    font-size: 28px;
    line-height: 34px;
  }

  @media only screen and (max-width: 600px) {
    font-size: 24px;
    line-height: 28px;
  }
}

p, .info-text ::v-deep p {
  font-family: $body-font-family;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 25px;
  color: $fin-color-primary-light;

  @media only screen and (max-width: 600px) {
    font-size: 14px;
    line-height: 19px;
  }
}

ul li, .info-text ::v-deep ul li {
  font-family: $body-font-family;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 25px;
  color: $fin-color-primary-light;

  @media only screen and (max-width: 600px) {
    font-size: 14px;
    line-height: 19px;
  }
}

.content-padding {
  padding-left: 48px;
  padding-right: 48px;

  @media only screen and (max-width: 600px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.action-btn {
  margin-left: 48px;
  margin-right: 48px;
  margin-bottom: 48px;

  @media only screen and (max-width: 600px) {
    margin-left: 24px;
    margin-right: 24px;
    margin-bottom: 24px;
  }
}
</style>
